import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import api from "api/api";

import { AgGridReact } from "ag-grid-react"; // React Data Grid Component

// mui components
import Button from "@mui/material/Button";

// mui icons
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";

// component
import TagsAddUpdateComponent from "./TagsAddUpdateComponent";
import ClaimsDataDeleteComponent from "../common/ClaimsDataDeleteComponent";
import Loader from "../common/Loader";
import AgGridCheckboxFilter from "components/common/AgGridCheckboxFilter";

import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

function Tags({ selectedStudyType = {}, selectedStudyTypeMethod = {} }) {
    console.log("selectedStudyTypeMethod", selectedStudyTypeMethod);
    const dispatch = useDispatch();

    const gridRef = useRef();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const [claimsTerritoryTags, setClaimsTerritoryTags] = useState([]);
    const [claimsTerritorySelectedTags, setClaimsTerritorySelectedTags] = useState([]);

    const [allRowData, setAllRowData] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowToUpdate, setSelectedRowToUpdate] = useState([]);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const arr = Array.from(new Set(rowData.map((ele) => ele.title).sort()));
        setClaimsTerritoryTags(arr);
    }, [allRowData]);

    useEffect(() => {
        if (claimsTerritorySelectedTags.length !== 0) {
            // works as or operator
            const filteredData = allRowData.filter((item) => claimsTerritorySelectedTags.includes(item.title)) || [];
            setRowData([...filteredData]);
        } else {
            setRowData([...allRowData]);
        }
        setSelectedRows([]);
    }, [claimsTerritorySelectedTags, allRowData]);

    const getSelectedTags = (values) => {
        setClaimsTerritorySelectedTags([...values]);
    };

    const multipleCellRenderer = (params) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
            return (
                <div className="ag-multi-line-cell-render">
                    {params.value.map((val, i) => {
                        return (
                            <p
                                key={i}
                                style={{
                                    paddingTop: 5,
                                }}
                            >
                                {val}
                            </p>
                        );
                    })}
                </div>
            );
        } else {
            return params.value;
        }
    };

    const handleUpdateClick = (row) => {
        setSelectedRowToUpdate(row.data);
        setIsUpdateDialogOpen(true);
    };

    const columnDefs = [
        {
            headerName: "Claims Territory tags(s)",
            field: "title",
            minWidth: 300,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            floatingFilter: true,
            filter: AgGridCheckboxFilter,
            filterParams: {
                values: claimsTerritoryTags,
                getSelectedValues: getSelectedTags,
            },
        },
        {
            headerName: "Claims Statements",
            field: "statement__title",
            minWidth: 720,
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Study Reference",
            field: "study_reference__title",
            minWidth: 420,
            cellRenderer: multipleCellRenderer,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"], // to get filter remove class .ag-floating-filter-button  form css
                // filterOptions: ["equals", "contains", "startsWith", "endsWith"], // to get filter remove class .ag-floating-filter-button  form css
            },
        },
        {
            headerName: "Action",
            // field: "claims_tag_mapping__title",
            minWidth: 120,
            // returnEditIcon: (row, index) => {
            cellRenderer: (row) => (
                <EditIcon
                    color="primary"
                    style={{
                        cursor: "pointer",
                    }}
                    onClick={() => handleUpdateClick(row)}
                />
            ),
        },
    ];

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            sortable: true,
            resizable: true,
            cellStyle: {
                fontSize: "14px",
                border: "none",
            },
            minWidth: 200,
        };
    }, []);

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const onSelectionChanged = useCallback(() => {
        let _selectedRows = gridRef.current.api.getSelectedRows();
        setSelectedRows(_selectedRows);
    }, []);

    const fetchData = useCallback(() => {
        const studyTypeMethods = [...new Set(selectedStudyTypeMethod.map((datum) => datum.value))];
        if (selectedStudyType?.value && studyTypeMethods.length > 0) {
            setIsLoading(true);
            let url = process.env.REACT_APP_SERVER_PATH + `/claims-tags/claims-tag-statement/`;
            let params = [];
            if (productCategoryId) {
                params.push(`product_category=${productCategoryId}`);
            }
            if (selectedStudyType?.value) {
                params.push(`study_type=${selectedStudyType.value}`);
            }
            if (selectedStudyTypeMethod?.value) {
                params.push(`&study_type_methods=${studyTypeMethods.join(",")}`);
            }
            if (params.length > 0) {
                url += `?` + params.join("&");
            }

            try {
                api.get(url)
                    .then(async (res) => {
                        setAllRowData([...res.data]);
                        setIsLoading(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    }, [productCategoryId, selectedStudyType?.value, selectedStudyTypeMethod]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="ag-theme-alpine"
                style={{
                    height: "calc(100vh - 295px)",
                    width: "100%",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: "-40px",
                        right: "0px",
                    }}
                >
                    <Button
                        size="small"
                        className="custom-btn custom-btn-outlined color-blue me-2"
                        variant="outlined"
                        startIcon={<ControlPointOutlinedIcon />}
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Add
                    </Button>
                    <Button
                        size="small"
                        className="custom-btn"
                        variant="outlined"
                        startIcon={<DeleteOutlinedIcon />}
                        style={selectedRows.length === 0 ? { color: "rgba(0, 0, 0, 0.26)", borderColor: "rgba(0, 0, 0, 0.12)" } : { color: "red", borderColor: "red" }}
                        onClick={() => setIsDeleteDialogOpen(true)}
                        disabled={selectedRows.length === 0}
                    >
                        Delete
                    </Button>
                </div>
                <style>
                    {`
                        .ag-header-group-cell-label {
                            justify-content: center !important;
                        }
                        .ag-header-cell-comp-wrapper {
                            justify-content: center !important;
                        }
                    `}
                </style>
                <AgGridReact
                    rowStyle={{
                        background: "white",
                    }}
                    ref={gridRef}
                    rowSelection="multiple"
                    rowMultiSelectWithClick={true}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={(params) => params.id}
                    onSelectionChanged={onSelectionChanged}
                    // sideBar={false}
                    rowHeight={35}
                    headerHeight={40}
                    floatingFiltersHeight={40}
                    suppressDragLeaveHidesColumns={true}
                    sortingOrder={sortingOrder}
                ></AgGridReact>
            </div>
            <div className="d-flex">
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        marginRight: 20,
                        fontWeight: 600,
                    }}
                >
                    Selected rows: {selectedRows.length}
                </span>
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        fontWeight: 600,
                    }}
                >
                    Total rows: {rowData.length}
                </span>
            </div>
            {isAddDialogOpen && (
                <TagsAddUpdateComponent
                    title="Add New Tags"
                    type="add"
                    isOpen={isAddDialogOpen}
                    setIsAddDialogOpen={setIsAddDialogOpen}
                    fetchData={fetchData}
                    selectedStudyType={selectedStudyType}
                    selectedStudyTypeMethod={selectedStudyTypeMethod}
                />
            )}
            {isUpdateDialogOpen && (
                <TagsAddUpdateComponent
                    title="Update Tags"
                    type="update"
                    selectedRows={selectedRowToUpdate}
                    isOpen={isUpdateDialogOpen}
                    setIsUpdateDialogOpen={setIsUpdateDialogOpen}
                    fetchData={fetchData}
                    selectedStudyType={selectedStudyType}
                    selectedStudyTypeMethod={selectedStudyTypeMethod}
                />
            )}
            {isDeleteDialogOpen && (
                <ClaimsDataDeleteComponent
                    title="The following tags are already present in the claim statements :"
                    component="tags"
                    selectedRows={selectedRows}
                    isOpen={isDeleteDialogOpen}
                    setIsDeleteDialogOpen={setIsDeleteDialogOpen}
                    fetchData={fetchData}
                />
            )}
        </>
    );
}
Tags.propTypes = {
    selectedStudyType: PropTypes.object,
    selectedStudyTypeMethod: PropTypes.object,
};

export default Tags;
